<template>
  <div ref="chatContentRef" :class="['chat-content']">
    <ChatComment
      v-for="comment in comments"
      :key="comment.id"
      :recipient-id="recipientId"
      :comment="comment"
      :comments="comments"
      :sender="sender"
      :recipients="recipients"
    />
  </div>
</template>

<script lang="ts">
import type { ChatComment as IChatComment } from '@getaccept/lib-shared/src/chat/types/chat-comment';
import type { SigningPageRecipient } from '@getaccept/lib-shared/src/types/signing-page-recipient';
import type { SigningPageUser } from '@getaccept/lib-shared/src/types/signing-page-user';
import type { PropType } from 'vue';
import { watch, onBeforeUnmount, onMounted, ref, defineComponent } from 'vue';
import ChatComment from './ChatComment.vue';

export default defineComponent({
  components: { ChatComment },
  props: {
    recipientId: {
      type: String,
      required: true,
    },
    sender: {
      type: Object as PropType<SigningPageUser>,
      required: true,
    },
    comments: {
      type: Array as PropType<IChatComment[]>,
      required: true,
    },
    recipients: {
      type: Array as PropType<SigningPageRecipient[]>,
      required: true,
    },
  },
  setup(props) {
    const chatContentRef = ref<Element>(null);

    const commentAdded = () => {
      setTimeout(() => {
        scrollToBottom();
      }, 100);
    };
    watch(() => props.comments, commentAdded);

    const scrollToBottom = () => {
      const container = chatContentRef.value;
      if (!container) {
        return;
      }
      container.scrollTop = container.scrollHeight;
    };

    const resizeHandler = () => {
      if (window.innerWidth <= 600) {
        scrollToBottom();
      }
    };

    onMounted(() => {
      scrollToBottom();
      window.addEventListener('resize', resizeHandler);
    });
    onBeforeUnmount(() => {
      window.removeEventListener('resize', resizeHandler);
    });

    return {
      chatContentRef,
      commentAdded,
      scrollToBottom,
      resizeHandler,
    };
  },
});
</script>

<style lang="scss" scoped>
.chat-content {
  width: 100%;
  height: 100%;
  display: flex;
  flex-direction: column;
  justify-content: flex-start;
  overflow-y: auto;
  padding: var(--chat-header-height) var(--spacing-100) var(--spacing-100) var(--spacing-100);
  box-sizing: border-box;
}
</style>
